import { create } from 'zustand';
import {
  getMemberOverview,
  getMemberEnrolledCourses,
  getMemberAssessments,
  getMemberCertificates,
  getMemberLearningHours,
  getEnrollableCourses,
  getAssignableAssessments,
  enrollMemberInCourse,
  assignAssessmentToMember,
  MemberOverview,
  MemberCourse,
  MemberAssessment,
  MemberCertificate,
  MemberLearningHours,
  EnrollableCourse,
  AssignableAssessment,
} from '@/services/PagesServices/OrganizationMemberActivityService';

interface State {
  overview: MemberOverview | null;
  courses: MemberCourse[];
  assessments: MemberAssessment[];
  certificates: MemberCertificate[];
  hours: MemberLearningHours | null;

  loadingOverview: boolean;
  loadingCourses: boolean;
  loadingAssessments: boolean;
  loadingCertificates: boolean;
  loadingHours: boolean;

  enrolling: boolean;
  assigning: boolean;

  error: string | null;

  fetchOverview: (userId: string) => Promise<void>;
  fetchCourses: (userId: string) => Promise<void>;
  fetchAssessments: (userId: string) => Promise<void>;
  fetchCertificates: (userId: string) => Promise<void>;
  fetchHours: (userId: string, days?: number) => Promise<void>;

  fetchEnrollable: (userId: string, search?: string) => Promise<EnrollableCourse[]>;
  fetchAssignable: (userId: string, search?: string) => Promise<AssignableAssessment[]>;

  enrollInCourse: (userId: string, courseId: number) => Promise<{ success: boolean; message: string }>;
  assignAssessment: (userId: string, formId: number, dueDate: string | null) => Promise<{ success: boolean; message: string }>;

  reset: () => void;
}

const useOrganizationMemberActivityStore = create<State>((set, get) => ({
  overview: null,
  courses: [],
  assessments: [],
  certificates: [],
  hours: null,

  loadingOverview: false,
  loadingCourses: false,
  loadingAssessments: false,
  loadingCertificates: false,
  loadingHours: false,

  enrolling: false,
  assigning: false,

  error: null,

  fetchOverview: async (userId) => {
    set({ loadingOverview: true, error: null });
    const res = await getMemberOverview(userId);
    set({
      overview: res.success ? res.data ?? null : null,
      loadingOverview: false,
      error: res.success ? null : res.error?.message ?? 'Failed to load overview',
    });
  },

  fetchCourses: async (userId) => {
    set({ loadingCourses: true });
    const res = await getMemberEnrolledCourses(userId);
    set({ courses: res.data ?? [], loadingCourses: false });
  },

  fetchAssessments: async (userId) => {
    set({ loadingAssessments: true });
    const res = await getMemberAssessments(userId);
    set({ assessments: res.data ?? [], loadingAssessments: false });
  },

  fetchCertificates: async (userId) => {
    set({ loadingCertificates: true });
    const res = await getMemberCertificates(userId);
    set({ certificates: res.data ?? [], loadingCertificates: false });
  },

  fetchHours: async (userId, days = 30) => {
    set({ loadingHours: true });
    const res = await getMemberLearningHours(userId, days);
    set({ hours: res.data ?? null, loadingHours: false });
  },

  fetchEnrollable: async (userId, search = '') => {
    const res = await getEnrollableCourses(userId, search);
    return res.data ?? [];
  },

  fetchAssignable: async (userId, search = '') => {
    const res = await getAssignableAssessments(userId, search);
    return res.data ?? [];
  },

  enrollInCourse: async (userId, courseId) => {
    set({ enrolling: true });
    const res = await enrollMemberInCourse(userId, courseId);
    set({ enrolling: false });
    if (res.success) {
      await get().fetchCourses(userId);
      return { success: true, message: `Enrolled in "${res.data?.title ?? 'course'}"` };
    }
    return { success: false, message: res.error?.message ?? 'Enrollment failed' };
  },

  assignAssessment: async (userId, formId, dueDate) => {
    set({ assigning: true });
    const res = await assignAssessmentToMember(userId, formId, dueDate);
    set({ assigning: false });
    if (res.success) {
      await get().fetchAssessments(userId);
      return { success: true, message: `Assigned "${res.data?.title ?? 'assessment'}"` };
    }
    return { success: false, message: res.error?.message ?? 'Assignment failed' };
  },

  reset: () => set({
    overview: null,
    courses: [],
    assessments: [],
    certificates: [],
    hours: null,
    error: null,
  }),
}));

export default useOrganizationMemberActivityStore;
